<div class="settingsDialog">
  <div class="dialogHeader">
    <h2 class="primary" i18n="@@Edit Facility Inventory Product">Edit Facility Inventory Product</h2>
    <h3 class="dialogSubtitle">{{facilityInventoryProduct.title}}</h3>
  </div>

  <!-- The reason an operator most often opens this dialog. Stated up
       front rather than left to be inferred from a badge, because the
       consequence (replenishment silently unavailable) is invisible
       everywhere else. -->
  <div class="banner bannerWarning" *ngIf="initialProductIsArchived">
    <mat-icon class="bannerIcon">archive</mat-icon>
    <span i18n="@@ArchivedPrimaryProductBanner">This inventory row points at an archived Product, so it can no longer be replenished. Choose a current Product below. The archived one is kept in the product history so past usage records still resolve.</span>
  </div>

  <form [formGroup]="formGroup" (change)="formChanged()">

    <!-- ============================ Product ======================= -->
    <section class="formSection">
      <h4 class="sectionHeading" i18n="@@Primary Product">Primary Product</h4>

      <div class="productSummary" *ngIf="selectedProduct">
        <span class="summaryField">
          <span class="summaryLabel" i18n="@@Product Id">Product Id</span>
          <span class="summaryValue">{{selectedProduct.ID | numberFormat}}</span>
        </span>
        <span class="summaryField" *ngIf="selectedProduct.book_ID">
          <span class="summaryLabel" i18n="@@Book Id">Book Id</span>
          <span class="summaryValue">{{selectedProduct.book_ID | numberFormat}}</span>
        </span>
        <span class="summaryField summaryFieldGrow">
          <span class="summaryLabel" i18n="@@Title">Title</span>
          <span class="summaryValue">{{selectedProduct.title}}</span>
        </span>
        <span class="archivedChip" *ngIf="selectedProductIsArchived" i18n="@@Archived Product">Archived Product</span>
      </div>

      <div class="pickerControls">
        <mat-form-field class="filterField">
          <input matInput
                 type="text"
                 [value]="productFilter"
                 (input)="onProductFilterChanged($event.target.value)"
                 i18n-placeholder="@@FilterProductsPlaceholder"
                 placeholder="Filter by title, product id or book id">
        </mat-form-field>
        <mat-checkbox class="archivedToggle"
                      [checked]="showArchivedProducts"
                      (change)="setShowArchivedProducts($event.checked)">
          <span i18n="@@Show archived products">Show archived products</span>
        </mat-checkbox>
      </div>

      <mat-form-field class="fullWidth">
        <mat-select i18n-placeholder="@@Select Product" placeholder="Select Product" formControlName="product_ID" required>
          <mat-option *ngFor="let p of candidateProducts" [value]="p.ID">
            <span class="optionId">{{p.ID | numberFormat}}</span>
            <span class="optionTitle">{{p.title}}</span>
            <span class="optionArchived" *ngIf="isArchived(p)" i18n="@@Archived Product">Archived Product</span>
          </mat-option>
        </mat-select>
      </mat-form-field>
      <div class="fieldHint" *ngIf="candidateProducts.length === 0" i18n="@@No matching products">No matching products</div>

      <div class="banner bannerInfo" *ngIf="productWasChanged">
        <mat-icon class="bannerIcon">history</mat-icon>
        <span i18n="@@ProductChangeNotice">Saving will make this the primary Product for this inventory row and move product id {{previousProductId | numberFormat}} into the product history.</span>
      </div>
    </section>

    <!-- ======================= Product history ==================== -->
    <section class="formSection" *ngIf="historicalProductIds.length > 0">
      <h4 class="sectionHeading" i18n="@@Product History">Product History</h4>
      <div class="fieldHint" i18n="@@ProductHistoryHint">Product ids this inventory row has used before. They are retained so historical usage records still resolve; archived entries here are expected and harmless.</div>
      <div class="historyList">
        <span class="historyChip" *ngFor="let id of historicalProductIds"
              [class.historyChipArchived]="isProductIdArchived(id)">
          <span class="historyChipId">{{id | numberFormat}}</span>
          <span class="historyChipTitle">{{getProductTitle(id)}}</span>
        </span>
      </div>
    </section>

    <!-- =========================== Settings ======================= -->
    <section class="formSection">
      <h4 class="sectionHeading" i18n="@@Settings">Settings</h4>
      <div class="fieldRow">
        <mat-form-field class="fieldHalf">
          <mat-select placeholder="State" i18n-placeholder="@@State" formControlName="state" required>
            <mat-option value="PREPARING" [disabled]="startupService.selectedRole === 'organization' && facilityInventoryProduct.state !== 'PREPARING'" i18n="@@Preparing">Preparing</mat-option>
            <mat-option value="ACTIVE" [disabled]="startupService.selectedRole === 'organization' && facilityInventoryProduct.state === 'PREPARING'" i18n="@@Active">Active</mat-option>
            <mat-option value="INACTIVE" i18n="@@Inactive">Inactive</mat-option>
            <mat-option value="RETIRE" i18n="@@Retire">Retire</mat-option>
          </mat-select>
        </mat-form-field>
        <mat-form-field class="fieldHalf">
          <mat-select placeholder="Inventory Management Type" i18n-placeholder="@@Inventory Management Type" formControlName="inventoryManagementType" required>
            <mat-option value="CUSTOMER" i18n="@@Customer">Customer</mat-option>
            <mat-option value="LEVEL" i18n="@@Level_">Level</mat-option>
            <mat-option value="ADHOC" i18n="@@Adhoc">Adhoc</mat-option>
          </mat-select>
        </mat-form-field>
      </div>
    </section>

    <!-- ========================== Placement ======================= -->
    <section class="formSection" *ngIf="startupService.selectedRole === 'facility'">
      <h4 class="sectionHeading" i18n="@@Placement">Placement</h4>
      <div class="fieldRow">
        <mat-form-field class="fieldHalf">
          <input matInput i18n-placeholder="@@location" placeholder="Location" id="location" required type="text" formControlName="location">
        </mat-form-field>
        <mat-form-field class="fieldHalf">
          <input matInput i18n-placeholder="@@Shelf" placeholder="Shelf" id="shelf" required type="text" formControlName="shelf">
        </mat-form-field>
      </div>
      <div class="fieldRow">
        <mat-form-field class="fieldHalf">
          <input matInput i18n-placeholder="@@Sequence" placeholder="Sequence" id="sequence" required type="number" min="0" max="1500" formControlName="sequence">
        </mat-form-field>
        <span class="fieldHalf"></span>
      </div>
    </section>

    <!-- ====================== Inventory controls ================== -->
    <section class="formSection">
      <h4 class="sectionHeading" i18n="@@Inventory Controls">Inventory Controls</h4>
      <div class="fieldRow">
        <mat-form-field class="fieldHalf">
          <input matInput i18n-placeholder="@@Reorder Quantity" placeholder="Reorder Quantity" id="reorderQuantity" required type="number" min="4" max="1500" formControlName="reorderQuantity">
        </mat-form-field>
        <mat-form-field class="fieldHalf">
          <input matInput i18n-placeholder="@@Reorder Level" placeholder="Reorder Level" id="reorderLevel" required type="number" min="0" max="1500" formControlName="reorderLevel">
        </mat-form-field>
      </div>
      <div class="fieldRow" *ngIf="startupService.selectedRole === 'facility'">
        <mat-form-field class="fieldHalf">
          <input matInput i18n-placeholder="@@Case Quantity" placeholder="Case Quantity" id="caseQuantity" required type="number" min="0" max="1500" formControlName="caseQuantity">
        </mat-form-field>
        <span class="fieldHalf"></span>
      </div>
    </section>

    <div class="dialogActions">
      <button mat-raised-button type="button" mat-dialog-close="" i18n="dialog dismissal|Button that closes a dialog and discards any unsaved edits. It never removes or destroys existing data - the Delete button does that. Must translate to a word meaning 'abandon this action', not 'erase'.@@Cancel">Cancel</button>
      <button mat-raised-button type="submit" color="primary" [disabled]="formGroup.invalid" (click)="onSave()" i18n="@@Save Facility Inventory Product">Save Facility Inventory Product</button>
    </div>
  </form>
</div>
